import { useNavigate } from "react-router-dom";
import { PAGE_NAME_INFERENCE_FACTOR_ITEM } from "../../../../constants/common_constants";
import { IInferenceFactorItemListType } from "../types/select_phenomenon_type";
import { useDtsAppContext } from "../../../../components/provider/DtsAppContextProvider";
import {
  getInferenceFactor,
} from "../../../api/dts_api_wrapper";
import { useTranslation } from "react-i18next";
import { useApi, useErrorDialog } from "../../../../hooks";
import { IInferenceFactorListType } from "../../inference_factors/types/inference_factor_type";

interface PhenomenonNextPageButtonProps {
  disabled: boolean;
  userId: string;
  model: string;
  phenomenon: string[];
  language: string;
  modelType: string;
}

// ボタン
export const PhenomenonNextPageButton: React.FC<
  PhenomenonNextPageButtonProps
> = ({
  disabled,
  userId,
  model,
  phenomenon,
  language,
  modelType,
}) => {
  const { setDacInferenceFactor } = useDtsAppContext();
  // ページ遷移用
  const navigate = useNavigate();
  //言語切替用
  const { t } = useTranslation();

  const { handleApi } = useApi();
  // エラーダイアログ
  const { showErrorDialog } = useErrorDialog();

  const handleButtonClick = async () => {
    try {
      if (!userId) {
        //何らかの理由でuserIdが読み込めなかった
        throw new Error("userId is unknown");
      }
      if (!model) {
        //何らかの理由でmodelが読み込めなかった
        throw new Error("model is unknown");
      }

      //推定要因項目取得
      const inferenceFactorResult = await handleApi(
        getInferenceFactor,
        model,
        phenomenon,
        modelType,
        language,
      );
      //推定要因項目取得エラーの場合
      if ("errorMessage" in inferenceFactorResult) {
        console.log("errorMessage: ", inferenceFactorResult.errorMessage);
        throw new Error("failed to get inferenceFactor");
      }
      //データが空かどうか判別, エラーダイアログ表示(その場にとどまる)
      const inferenceFactorToJson: IInferenceFactorListType = JSON.parse(
        JSON.stringify(inferenceFactorResult),
      );
      if (!inferenceFactorToJson.inferenceFactor.length)
        throw new Error("inferenceFactorItems is empty");
      //ソートした推定要因項目をContext+ローカルストレージに保存
      sortInferenceFactorData(inferenceFactorResult);
      const inferenceFactorStr = JSON.stringify(inferenceFactorResult);
      setDacInferenceFactor(inferenceFactorStr);
      localStorage.setItem("inferenceFactor", inferenceFactorStr);

      localStorage.setItem("selectedPhenomenonName",JSON.stringify(phenomenon));   // 2026年8月追加

      navigate(`/${PAGE_NAME_INFERENCE_FACTOR_ITEM}`);
    } catch (err) {
      console.log("[Phenomenon Selection]handleApi err :", err);
      if (!userId || !model) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
          "navigateToTopPage",
        ); //エラー", "通信エラーが発生しました。(トップページに遷移する)
      } else if (
        err instanceof Error &&
        err.message === "inferenceFactorItems is empty"
      ) {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("型式選択画面.要因が見つかりません。"),
          "",
        ); //エラー", "要因が見つかりません。"
      } else if (
        err instanceof Error &&
        (err.message === "Expired token" || err.message === "Invalid token")
      ) {
        //LocalStorageのセッション情報クリア
        const userpool_client_id =
          process.env.REACT_APP_AWS_USER_POOLS_WEB_CLIENT_ID ?? "";
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.accessToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.refreshToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.idToken`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.clockDrift`
        );
        localStorage.removeItem(
          `CognitoIdentityServiceProvider.${userpool_client_id}.LastAuthUser`
        );

        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.セッションがタイムアウトしました。"),
          "ydsyos"
        ); //エラー", "セッションがタイムアウトしました。(YDS/YOSに遷移する)
      } else {
        showErrorDialog(
          t("共通エラーポップ.エラー"),
          t("共通エラーポップ.通信エラーが発生しました。"),
        ); //エラー", "通信エラーが発生しました。
      }
    }
  };

  /* 取得した推定要因項目リストをpriorityに従ってソートする関数 */
  const sortInferenceFactorData = (
    unsortedData: IInferenceFactorItemListType,
  ) => {
    unsortedData.inferenceFactor.sort((a, b) =>
      a.issueFactor.priority < b.issueFactor.priority ? -1 : 1,
    );
    unsortedData.inferenceFactor.map((value, index) => {
      return value.issueFactor.repairFactor.referenceInfo.sort((a, b) =>
        a.priority < b.priority ? -1 : 1,
      );
    });
    return unsortedData;
  };

  return (
    <div className="flex justify-center">
      <button
        disabled={disabled}
        className={`${
          !disabled
            ? "border-main-red bg-white"
            : "border-gray-500 bg-gray-300 text-gray-500"
        } rounded-full border-2 py-1 px-6 w-40 overflow-hidden whitespace-nowrap`}
        onClick={handleButtonClick}
      >
        <p className="font-bold text-[20px]">{t("現象選択画面.次へ")}</p>
        {/* 次へ */}
      </button>
    </div>
  );
};
